import React, { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import client from '../../API';
import UploadFileDialog from '../../Components/tools/uploadfile';
import CreateAssetDialog from '../../Components/tools/createasset';
// import CreateContractDialog from '../../Components/tools/createcontract'; // DISABLED: Contract functionality
import AssetOrganizerSeg from '../Files/AssetOrganizerSeg';

import {
  AppBar,
  Toolbar,
  Button,
  FormControlLabel,
  Switch,
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  IconButton,
  Container,
  Checkbox,
  FormControl,
  Select,
  MenuItem,
  Snackbar,
  Fab,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  useMediaQuery
} from '@mui/material';

import {
  PictureAsPdf,
  Description,
  Image,
  UploadFile,
  // Assignment, // DISABLED: Contract functionality
  Search,
  ViewModule as GridViewIcon,
  ViewList as ListViewIcon,
  FilterList as FilterIcon,
  Visibility as ViewIcon,
  Add as AddIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  ExpandLess,
  ExpandMore
} from '@mui/icons-material';

import { useTheme } from '@mui/material/styles';

const AssetsManager = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  
  // View state
  const [viewMode, setViewMode] = useState('grid');
  const [viewFilter, setViewFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectionMode, setSelectionMode] = useState(false);
  const [showOrganizer, setShowOrganizer] = useState(true);
  
  // Mobile specific states
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [actionsExpanded, setActionsExpanded] = useState(false);
  
  // Dialog states
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [createAssetDialogOpen, setCreateAssetDialogOpen] = useState(false);
  // const [createContractDialogOpen, setCreateContractDialogOpen] = useState(false); // DISABLED: Contract functionality
  
  // Notification state
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // API Queries
  const {
    data: files,
    isLoading: filesLoading,
    error: filesError,
    refetch: refetchFiles
  } = useQuery('files', () => client.files.fetch(''), {
    initialData: [],
    initialDataUpdatedAt: 0
  });

  // DISABLED: Contract functionality - commenting out to prevent 401 errors
  /*
  const {
    data: contractsRes,
    isLoading: contractsLoading,
    error: contractsError,
    refetch: refetchContracts
  } = useQuery('contracts', () => client.files.fetchContracts(), {
    initialData: { data: [] },
    initialDataUpdatedAt: 0
  });
  */

  // Helper Functions
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const isImageFile = (filename) => {
    const imageExtensions = ['.png', '.jpg', '.jpeg', '.gif', '.bmp', '.webp'];
    return imageExtensions.some((ext) => filename?.toLowerCase().endsWith(ext));
  };

  const determineFileType = (filename) => {
    if (filename?.toLowerCase().endsWith('.html') || filename?.toLowerCase().endsWith('.htm')) {
      return 'html';
    }
    if (filename?.toLowerCase().endsWith('.pdf')) {
      return 'pdf';
    }
    if (isImageFile(filename)) {
      return 'image';
    }
    return 'misc';
  };

  // Selection functions
  const toggleItemSelection = (itemId, itemType) => {
    if (itemType === 'file') {
      setSelectedItems(prev => {
        const itemKey = `${itemType}_${itemId}`;
        return prev.includes(itemKey) 
          ? prev.filter(id => id !== itemKey)
          : [...prev, itemKey];
      });
    }
  };

  const isItemSelected = (itemId, itemType) => {
    return selectedItems.includes(`${itemType}_${itemId}`);
  };

  const getSelectedFiles = () => {
    return selectedItems
      .filter(item => item.startsWith('file_'))
      .map(item => parseInt(item.split('_')[1]));
  };

  // Event handlers
  const handleItemClick = (item, itemType) => {
    if (selectionMode && itemType === 'file') {
      toggleItemSelection(item.id, itemType);
    } else {
      // DISABLED: Contract functionality
      /*
      if (item.type === 'contract') {
        navigate(`/contract/${item.id}`);
      } else {
        navigate(`/files/${item.id}`);
      }
      */
      navigate(`/files/${item.id}`);
    }
  };

  const handleSuccess = () => {
    queryClient.invalidateQueries('files');
    // queryClient.invalidateQueries('contracts'); // DISABLED: Contract functionality
    queryClient.invalidateQueries('organizers');
  };

  // Dialog Handlers
  const openUploadDialog = () => {
    setUploadDialogOpen(true);
    setMobileMenuOpen(false);
  };
  const closeUploadDialog = () => {
    setUploadDialogOpen(false);
    handleSuccess();
  };

  const openCreateAssetDialog = () => {
    setCreateAssetDialogOpen(true);
    setMobileMenuOpen(false);
  };
  const closeCreateAssetDialog = () => {
    setCreateAssetDialogOpen(false);
    handleSuccess();
  };

  // DISABLED: Contract functionality
  /*
  const openCreateContractDialog = () => {
    setCreateContractDialogOpen(true);
    setMobileMenuOpen(false);
  };
  const closeCreateContractDialog = () => {
    setCreateContractDialogOpen(false);
    handleSuccess();
  };
  */

  // Render functions
  const renderFileIcon = (file, size = 'large') => {
    const iconProps = { fontSize: size === 'large' ? 'large' : 'medium' };
    // if (file.type === 'contract') return <Assignment {...iconProps} color="primary" />; // DISABLED: Contract functionality
    if (file.fileType === 'image') return <Image {...iconProps} />;
    if (file.fileType === 'pdf') return <PictureAsPdf {...iconProps} />;
    return <Description {...iconProps} />;
  };

  // Data processing
  // DISABLED: Contract functionality
  /*
  const contracts = (contractsRes?.data || []).map((contract) => ({
    ...contract,
    type: 'contract',
    filename: `${contract.name}.pdf`,
    fileType: 'contract',
    creation_date: contract.created_at
  }));
  */

  const processedFiles = (files || []).map((file) => ({
    ...file,
    fileType: determineFileType(file.filename)
  }));

  // DISABLED: Contract functionality - only use files now
  // const mergedData = [...processedFiles, ...contracts];
  const mergedData = [...processedFiles];

  // Filter files for display
  const filteredFiles = searchQuery
    ? mergedData.filter(file =>
        (file.name || file.filename).toLowerCase().includes(searchQuery.toLowerCase())
      )
    : mergedData;

  // DISABLED: Contract functionality - remove contractsLoading and contractsError
  // const isLoading = filesLoading || contractsLoading;
  // const error = filesError || contractsError;
  const isLoading = filesLoading;
  const error = filesError;

  // Mobile menu content
  const renderMobileMenu = () => (
    <Drawer
      anchor="right"
      open={mobileMenuOpen}
      onClose={() => setMobileMenuOpen(false)}
      sx={{ '& .MuiDrawer-paper': { width: 280, p: 2 } }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Menu</Typography>
        <IconButton onClick={() => setMobileMenuOpen(false)}>
          <CloseIcon />
        </IconButton>
      </Box>

      <List>
        <ListItem sx={{ px: 0 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search files..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
          />
        </ListItem>

        <ListItem button onClick={() => setActionsExpanded(!actionsExpanded)} sx={{ px: 0 }}>
          <ListItemText primary="Actions" />
          {actionsExpanded ? <ExpandLess /> : <ExpandMore />}
        </ListItem>
        
        <Collapse in={actionsExpanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItem button onClick={openUploadDialog} sx={{ pl: 2 }}>
              <ListItemIcon><UploadFile /></ListItemIcon>
              <ListItemText primary="Upload Files" />
            </ListItem>
            <ListItem button onClick={openCreateAssetDialog} sx={{ pl: 2 }}>
              <ListItemIcon><Description /></ListItemIcon>
              <ListItemText primary="Create Asset" />
            </ListItem>
            {/* DISABLED: Contract functionality */}
            {/*
            <ListItem button onClick={openCreateContractDialog} sx={{ pl: 2 }}>
              <ListItemIcon><Assignment /></ListItemIcon>
              <ListItemText primary="Create Contract" />
            </ListItem>
            */}
          </List>
        </Collapse>

        <ListItem sx={{ px: 0, flexDirection: 'column', alignItems: 'stretch' }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>View Mode</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant={viewMode === 'grid' ? 'contained' : 'outlined'}
              onClick={() => setViewMode('grid')}
              startIcon={<GridViewIcon />}
              size="small"
              fullWidth
            >
              Grid
            </Button>
            {!isSmall && (
              <Button
                variant={viewMode === 'list' ? 'contained' : 'outlined'}
                onClick={() => setViewMode('list')}
                startIcon={<ListViewIcon />}
                size="small"
                fullWidth
              >
                List
              </Button>
            )}
          </Box>
        </ListItem>

        <ListItem sx={{ px: 0 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={selectionMode}
                onChange={(e) => {
                  setSelectionMode(e.target.checked);
                  setSelectedItems([]);
                }}
              />
            }
            label="Selection Mode"
          />
        </ListItem>

        <ListItem button onClick={() => setShowOrganizer(true)} sx={{ px: 0 }}>
          <ListItemIcon><FilterIcon /></ListItemIcon>
          <ListItemText primary="Enable Folder View" />
        </ListItem>
      </List>
    </Drawer>
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ margin: 2 }}>
        {error.message}
      </Alert>
    );
  }

  // If organizer view is enabled, delegate to AssetOrganizer
  if (showOrganizer) {
    return (
      <AssetOrganizerSeg 
        onToggleView={() => setShowOrganizer(false)}
        onRefresh={handleSuccess}
        files={mergedData}
      />
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      width: '100%'
    }}>
      {/* Top Navigation Bar */}
      <AppBar position="sticky" sx={{ top: 0, zIndex: 1100 }}>
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }}>
          <Typography 
            variant={isMobile ? "subtitle1" : "h6"} 
            sx={{ 
              fontWeight: 'bold', 
              flexGrow: 1,
              fontSize: { xs: '1rem', sm: '1.25rem' },
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {isMobile ? 'Assets' : 'Asset Manager - Simple View'}
          </Typography>
          
          {/* Desktop Search and View Controls */}
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <TextField
                size="small"
                placeholder="Search files..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ 
                  minWidth: 250,
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
                    '&.Mui-focused fieldset': { borderColor: 'white' }
                  },
                  '& .MuiInputBase-input': { color: 'white' },
                  '& .MuiInputBase-input::placeholder': { color: 'rgba(255,255,255,0.7)', opacity: 1 }
                }}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'rgba(255,255,255,0.7)' }} />,
                }}
              />
              
              <IconButton
                onClick={() => setViewMode('grid')}
                sx={{ color: viewMode === 'grid' ? 'white' : 'rgba(255,255,255,0.7)' }}
              >
                <GridViewIcon />
              </IconButton>
              
              <IconButton
                onClick={() => setViewMode('list')}
                sx={{ color: viewMode === 'list' ? 'white' : 'rgba(255,255,255,0.7)' }}
              >
                <ListViewIcon />
              </IconButton>
            </Box>
          )}

          {/* Mobile Menu Button */}
          {isMobile && (
            <IconButton
              onClick={() => setMobileMenuOpen(true)}
              sx={{ color: 'white' }}
            >
              <MenuIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container 
        maxWidth="xl" 
        sx={{ 
          px: { xs: 2, sm: 3, md: 3 },
          py: { xs: 2, sm: 3 },
          flex: 1,
          overflow: 'auto',
          width: '100%',
          maxWidth: '100%'
        }}
      >
        {/* Desktop Action bar */}
        {!isMobile && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => setShowOrganizer(true)}
                size="small"
              >
                Enable Folder View
              </Button>
              
              {selectionMode && selectedItems.length > 0 && (
                <Chip 
                  label={`${getSelectedFiles().length} files selected`} 
                  color="primary" 
                />
              )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectionMode}
                    onChange={(e) => {
                      setSelectionMode(e.target.checked);
                      setSelectedItems([]);
                    }}
                    size="small"
                  />
                }
                label="Select"
                sx={{ mr: 1 }}
              />

              <Button
                variant="outlined"
                onClick={openCreateAssetDialog}
                size="small"
              >
                Create Asset
              </Button>

              {/* DISABLED: Contract functionality */}
              {/*
              <Button
                variant="outlined"
                onClick={openCreateContractDialog}
                size="small"
              >
                Create Contract
              </Button>
              */}

              <Button
                variant="contained"
                startIcon={<UploadFile />}
                onClick={openUploadDialog}
                size="small"
              >
                Upload
              </Button>
            </Box>
          </Box>
        )}

        {/* Mobile Search Bar */}
        {isMobile && (
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search files..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          </Box>
        )}

        {/* Mobile Selection Info */}
        {isMobile && selectionMode && selectedItems.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Chip 
              label={`${getSelectedFiles().length} files selected`} 
              color="primary" 
              size="small"
            />
          </Box>
        )}

        {/* Files Display */}
        {(viewMode === 'grid' || isMobile) ? (
          <Box sx={{ width: '100%', overflow: 'hidden' }}>
            <Typography 
              variant={isMobile ? "subtitle1" : "h6"} 
              sx={{ 
                mb: 2, 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                fontSize: { xs: '1.1rem', sm: '1.25rem' },
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              All Files ({filteredFiles.length})
              {selectionMode && !isMobile && (
                <Chip 
                  size="small" 
                  label={`${getSelectedFiles().length} selected`} 
                  color="primary" 
                />
              )}
            </Typography>
            <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ width: '100%', margin: 0 }}>
              {filteredFiles.map(file => (
                <Grid 
                  item 
                  xs={6} 
                  sm={4} 
                  md={3} 
                  lg={2}
                  xl={2}
                  key={file.id}
                  sx={{ 
                    width: '100%',
                    maxWidth: '100%'
                  }}
                >
                  <Card
                    onClick={() => handleItemClick(file, 'file')}
                    sx={{
                      cursor: 'pointer',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'all 0.2s',
                      border: isItemSelected(file.id, 'file') ? 2 : 0,
                      borderColor: 'primary.main',
                      bgcolor: isItemSelected(file.id, 'file') ? 'action.selected' : 'background.paper',
                      overflow: 'hidden',
                      width: '100%',
                      maxWidth: '100%',
                      '&:hover': {
                        transform: isMobile ? 'none' : 'translateY(-2px)',
                        boxShadow: isMobile ? 2 : 4
                      }
                    }}
                  >
                    <Box sx={{ 
                      position: 'relative', 
                      height: { xs: 80, sm: 120 }, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      p: { xs: 0.5, sm: 1 },
                      overflow: 'hidden'
                    }}>
                      {selectionMode && (
                        <Checkbox
                          checked={isItemSelected(file.id, 'file')}
                          onChange={() => toggleItemSelection(file.id, 'file')}
                          sx={{ 
                            position: 'absolute', 
                            top: { xs: 2, sm: 4 }, 
                            right: { xs: 2, sm: 4 }, 
                            zIndex: 1,
                            '& .MuiSvgIcon-root': { fontSize: { xs: '1.2rem', sm: '1.5rem' } }
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      )}
                      {isImageFile(file.filename) ? (
                        <img
                          src={file.file_url}
                          alt={file.filename}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            borderRadius: '4px'
                          }}
                        />
                      ) : (
                        renderFileIcon(file, isMobile ? 'medium' : 'large')
                      )}
                    </Box>
                    <CardContent sx={{ 
                      pt: { xs: 0.5, sm: 1 }, 
                      pb: { xs: '8px !important', sm: '12px !important' },
                      px: { xs: 1, sm: 2 },
                      overflow: 'hidden',
                      width: '100%'
                    }}>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          display: 'block',
                          fontSize: { xs: '0.7rem', sm: '0.75rem' },
                          lineHeight: 1.2,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          width: '100%'
                        }}
                        title={file.name || file.filename}
                      >
                        {file.name || file.filename}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        color="text.secondary" 
                        sx={{ 
                          fontSize: { xs: '0.6rem', sm: '0.7rem' },
                          display: 'block',
                          mt: 0.5,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          width: '100%'
                        }}
                      >
                        {new Date(file.created_at || file.creation_date).toLocaleDateString()}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        ) : (
          /* Desktop List View */
          !isMobile && (
            <TableContainer component={Paper} sx={{ borderRadius: 2, overflow: 'auto' }}>
              <Table>
                <TableHead sx={{ backgroundColor: theme.palette.action.hover }}>
                  <TableRow>
                    {selectionMode && <TableCell padding="checkbox"></TableCell>}
                    <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Modified</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredFiles.map(file => (
                    <TableRow 
                      key={`file_${file.id}`}
                      hover
                      onClick={() => handleItemClick(file, 'file')}
                      sx={{ 
                        cursor: 'pointer',
                        bgcolor: isItemSelected(file.id, 'file') ? 'action.selected' : 'transparent'
                      }}
                    >
                      {selectionMode && (
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={isItemSelected(file.id, 'file')}
                            onChange={() => toggleItemSelection(file.id, 'file')}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </TableCell>
                      )}
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {renderFileIcon(file, 'medium')}
                          <Typography sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {file.name || file.filename}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={file.fileType?.toUpperCase() || 'File'} 
                          size="small" 
                          variant="outlined" 
                          color="default"
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(file.created_at || file.creation_date).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <IconButton size="small">
                          <ViewIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )
        )}

        {/* Empty State */}
        {filteredFiles.length === 0 && (
          <Box sx={{ textAlign: 'center', py: { xs: 4, sm: 8 } }}>
            <Typography 
              variant={isMobile ? "h6" : "h5"} 
              color="text.secondary" 
              gutterBottom
            >
              {searchQuery ? 'No results found' : 'No files found'}
            </Typography>
            <Typography 
              color="text.secondary" 
              sx={{ mb: 3, fontSize: { xs: '0.9rem', sm: '1rem' } }}
            >
              {searchQuery 
                ? 'Try adjusting your search terms' 
                : 'Upload files to get started'
              }
            </Typography>
            {!searchQuery && (
              <Button
                variant="contained"
                onClick={openUploadDialog}
                startIcon={<UploadFile />}
                size={isMobile ? "medium" : "large"}
              >
                Upload Files
              </Button>
            )}
          </Box>
        )}
        
        {/* Bottom spacer for mobile FAB */}
        {isMobile && <Box sx={{ height: 80 }} />}
      </Container>

      {/* Mobile FAB for quick upload */}
      {isMobile && (
        <Fab
          color="primary"
          aria-label="upload"
          onClick={openUploadDialog}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: 1200
          }}
        >
          <AddIcon />
        </Fab>
      )}

      {/* Mobile Menu Drawer */}
      {isMobile && renderMobileMenu()}

      {/* Dialogs */}
      <UploadFileDialog 
        open={uploadDialogOpen} 
        onClose={closeUploadDialog}
      />
      <CreateAssetDialog 
        open={createAssetDialogOpen} 
        onClose={closeCreateAssetDialog} 
      />
      {/* DISABLED: Contract functionality */}
      {/*
      <CreateContractDialog 
        open={createContractDialogOpen} 
        onClose={closeCreateContractDialog} 
        onSuccess={handleSuccess} 
      />
      */}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({...snackbar, open: false})}
        anchorOrigin={{ 
          vertical: isMobile ? 'top' : 'bottom', 
          horizontal: 'center' 
        }}
        sx={{ zIndex: 1300 }}
      >
        <Alert 
          onClose={() => setSnackbar({...snackbar, open: false})} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AssetsManager;